import styled, { css } from 'styled-components';
import { darken } from 'polished';

/* ===== Identidade visual (alinhada ao login / Header) =====
   --primary: #ff6d3c   --muted: #79869e
   --border:  rgba(216, 224, 235, 0.9)
   --row-hover: rgba(255, 109, 60, 0.05)
   Cores semânticas dos badges preservadas, apenas mais legíveis.
=========================================================== */

export const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0 10px;
  width: 100%;

  ${(props) =>
    props.withCollapse &&
    css`
      border-collapse: collapse;
      border-spacing: 0 10px;
    `}

  & > thead {
    & > th {
      padding: 0 25px 0;
      color: #79869e;
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      text-align: left;
    }
  }
`;

Table.Wrapper = styled.div`
  padding: 10px 5px;
  margin: 5px 0;

  ${(props) =>
    props.withBorder &&
    css`
      border: 1px solid rgba(216, 224, 235, 0.9);
      border-radius: 16px;
      background: #fff;
      box-shadow: 0 10px 30px rgba(23, 38, 59, 0.06);
    `}

  ${(props) =>
    props.minHeight &&
    css`
      height: calc(100% - 5px);
      overflow-y: auto;
    `}
`;

Table.List = styled(Table)`
  border-spacing: 0;

  & > tbody {
    & > tr {
      & > td {
        padding: 0 25px 0;
        color: #17263b;
        font-size: 18px;
        text-align: left;
      }

      :hover {
        background: rgba(255, 109, 60, 0.05);
      }
    }
  }
`;

Table.Action = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 0 10px;

  > button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 9px;
    background: none;
    cursor: pointer;
  }
`;

Table.Status = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

Table.BtnRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  color: #cbd0d8;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  :hover {
    color: ${darken(0.05, '#de3b3b')};
    background: rgba(222, 59, 59, 0.12);
    transform: translateY(-1px);
  }
`;

Table.BtnEdit = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  color: #b6bdca;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  :hover {
    color: #538061;
    background: rgba(83, 128, 97, 0.12);
    transform: translateY(-1px);
  }
`;

Table.Row = styled.tr``;

Table.Row.Kind = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 6px 12px;
  border-radius: 999px;
  text-align: center;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;

  ${(props) =>
    props.isAdded &&
    css`
      color: #2f7d52;
      background: rgba(83, 128, 97, 0.12);
      border: 1px solid rgba(83, 128, 97, 0.5);
    `};

  ${(props) =>
    props.isRemoved &&
    css`
      color: #c0291c;
      background: rgba(176, 24, 16, 0.1);
      border: 1px solid rgba(176, 24, 16, 0.45);
    `}

  ${(props) =>
    props.isPending &&
    css`
      color: #a87a12;
      background: rgba(214, 167, 23, 0.14);
      border: 1px solid rgba(214, 167, 23, 0.5);
    `}
`;

Table.RowList = styled.tr`
  background: #fff;
  color: #79869e;
  font-size: 14px;
  cursor: default;

  > td {
    ${(props) =>
      props.withPadding &&
      css`
        padding: 5px 10px 2px;
      `}
  }
`;

export const RowDefault = styled.tr`
  background: #fff;
  cursor: default;

  &:hover {
    background: rgba(255, 109, 60, 0.05);
  }

  & > td {
    padding: 0 25px;
    border: none;
  }
`;

export const RowMaster = styled.tr`
  background: #fff;
  cursor: pointer;
  height: ${(props) => (props.thin ? '25px' : '62px')};
  transition: background 0.18s ease;

  ${(props) =>
    props.noCursoPointer &&
    css`
      cursor: auto;
    `}

  &:hover {
    background: rgba(255, 109, 60, 0.05);
  }

  & > td {
    padding: ${(props) => (props.thin ? '7px 10px 6px' : '15px 20px 16px')};
    border-top: 1px solid rgba(216, 224, 235, 0.9);
    border-bottom: 1px solid rgba(216, 224, 235, 0.9);
    transition: border-color 0.18s ease;

    &:first-child {
      border-left: 1px solid rgba(216, 224, 235, 0.9);
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    &:last-child {
      border-right: 1px solid rgba(216, 224, 235, 0.9);
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }

  &:hover > td {
    border-color: rgba(255, 109, 60, 0.35);
  }
`;

export const Expand = styled.td`
  svg {
    transform: rotate(0deg);
    transition: all 0.2s ease-out;
  }

  ${(props) =>
    props.rotate === '1' &&
    css`
      > svg {
        transform: rotate(180deg);
      }
    `};
`;

export const RowDetail = styled.tr`
  background: #fff;

  & > td {
    padding: 20px 25px 16px;
    border-top: 1px solid rgba(216, 224, 235, 0.9);
    border-bottom: 1px solid rgba(216, 224, 235, 0.9);

    &:first-child {
      border-left: 1px solid rgba(216, 224, 235, 0.9);
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    &:last-child {
      border-right: 1px solid rgba(216, 224, 235, 0.9);
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }
`;

export const TitleOrder = styled.th`
  color: #79869e;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-align: left;

  ${(props) =>
    props.withPadding &&
    css`
      padding: 0 25px 0;
    `}

  button {
    border: none;
    background: none;
    color: #79869e;
    margin-left: 5px;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #ff6d3c;
    }
  }
`;

export const NotFound = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px 10px;
  color: #98a5b7;
  font-style: italic;
`;
